import React, { useContext, useEffect, useRef, useState } from "react";
import Skeleton from "react-loading-skeleton";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { addCart, delCart, setCart } from "../redux/action";
import { motion } from "framer-motion";
import { Footer, Navbar } from "../components";
import { AuthContext } from "../context/AuthContext";
import api from "../utils/base_url";
import toast from "react-hot-toast";
import ReviewBox from "../components/ReviewBox";
import StockNotifyModal from "../components/StockNotifyModal";
import "../styles/index.css";


const Product = () => {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [similarProducts, setSimilarProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingSimilar, setLoadingSimilar] = useState(false);
  const [stocks, setStocks] = useState({});
  const [variants, setVariants] = useState([]);
  const [showDescModal, setShowDescModal] = useState(false);
  const [isFavorite, setIsFavorite] = useState(false);
  const [stockError, setStockError] = useState({});
  const [disabledQty, setDisabledQty] = useState({});

  // ── Notify modal state ──────────────────────────────────────
  const [notifyOpen, setNotifyOpen] = useState(false);
  const [notifyProduct, setNotifyProduct] = useState(null);
  const [notifyForm, setNotifyForm] = useState({
    user_id: "",
    product_id: "",
    email: "",
    quantity: 1,
  });

  // Out-of-stock variant that the user hovered / the page auto-detects
  const [oosVariant, setOosVariant] = useState(null);

  const location = useLocation();

  const reviewRef = useRef(null);
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);

  const state = useSelector((state) => state.handleCart);
  const dispatch = useDispatch();

  const badgeStyleCurrent = {
    position: "absolute",
    top: "8px",
    right: "-12px",
    transform: "translateX(-50%)",
    padding: "6px 14px",
    background: "#dc3545",
    color: "white",
    borderRadius: "20px",
    fontSize: "0.8rem",
    fontWeight: 600,
    zIndex: 99,
    whiteSpace: "nowrap",
    boxShadow: "0 3px 10px rgba(0,0,0,0.2)",
  };

  // ── Helpers ─────────────────────────────────────────────────

  const isVariantOutOfStock = (v) => {
    const stockVal = stocks[v.id];
    if (stockVal !== undefined) return stockVal === 0;
    return !v.is_available || v.stock_quantity === 0;
  };

  const openNotifyForVariant = (v) => {
    setNotifyProduct(v);
    setNotifyForm({ user_id: user?.id ?? "", product_id: v.id, email: user?.email ?? "", quantity: 1 });
    setNotifyOpen(true);
  };

  // When the current product itself is OOS, surface the first available variant banner
  useEffect(() => {
    if (!product) return;
    const currentOos =
      !product.is_available || Number(stocks[product.id] ?? product.stock_quantity) === 0;

    if (currentOos && variants.length > 0) {
      // Find the OOS variant matching the current product (same id) for the notify banner
      const match = variants.find((v) => Number(v.id) === Number(id));
      setOosVariant(match ?? null);
    } else {
      setOosVariant(null);
    }
  }, [product, variants, stocks, id]);

  const handleShare = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({ title: product?.product_name, text: "Check this product", url });
      } catch { }
    } else {
      try {
        await navigator.clipboard.writeText(url);
        toast.success("Link copied!");
      } catch {
        toast.error("Copy failed");
      }
    }
  };

  const getCartQty = (productId) => {
    const item = state.find((p) => p.id === productId);
    return item ? item.qty : 0;
  };

  const goToReviews = () => {
    const position = reviewRef.current?.offsetTop - 80;
    window.scrollTo({ top: position, behavior: "smooth" });
  };

  const initiateCheckout = async () => {
    const token = sessionStorage.getItem("access");
    if (!token) {
      sessionStorage.setItem(
        "redirect_toast",
        "Please login first to continue checkout"
      );

      sessionStorage.setItem(
        "login_redirect",
        JSON.stringify({
          pathname: location.pathname,
          search: location.search,
          state: location.state,
        })
      );

      return navigate("/login");
    }
    const cartPayload = state.map((item) => ({ product_id: item.id, qty: item.qty }));
    try {
      const res = await api.post("checkout-initiate/", { user_id: user.id, cart: cartPayload });
      if (!res.data.status) { toast.error(res.data.message || "Checkout failed"); return; }
      if (res.data.updated_items?.length) toast.error("Some quantities were reduced due to low stock");
      if (res.data.removed_items?.length) toast.error("Some items removed because they are out of stock");
      const final = res.data.reserved_items.map((i) => ({
        id: i.product_id, qty: i.qty, product_name: i.product_name,
        price: Number(i.price) || 0, offer_price: i.offer_price ? Number(i.offer_price) : null,
        product_image: i.product_image, variant_label: i.variant_label ?? null,
      }));
      dispatch(setCart(final));
      navigate("/checkout");
    } catch (err) {
      console.error("initiateCheckout error:", err);
      toast.error("Unable to start checkout");
    }
  };

  const addItem = (product) => {
    const availableStock = stocks[product.id] ?? product.stock_quantity;
    if (availableStock && getCartQty(product.id) >= availableStock) {
      setStockError((prev) => ({ ...prev, [product.id]: true }));
      setDisabledQty((prev) => ({ ...prev, [product.id]: true }));
      setTimeout(() => {
        setStockError((prev) => ({ ...prev, [product.id]: false }));
        setDisabledQty((prev) => ({ ...prev, [product.id]: false }));
      }, 2000);
      return;
    }
    const productWithVariant = {
      ...product,
      variant_label: product.quantity_unit
        ? (product.quantity_unit.toLowerCase() === "size"
          ? product.quantity
          : `${parseFloat(product.quantity)} ${product.quantity_unit}`)
        : null,
    };
    dispatch(addCart(productWithVariant));
  };

  const removeItem = (product) => dispatch(delCart(product));

  useEffect(() => {
    const fetchProduct = async () => {
      setLoading(true);
      setLoadingSimilar(true);
      try {
        const res = await api.get(`/productlist/${id}/`);
        const data = res.data?.data;
        setProduct(data);

        if (data && !data.parent) {
          try {
            const resVar = await api.get(`/productvariantfilter/?parent_id=${data.id}`);
            const sortedVariants = (resVar.data?.data || []).sort(
              (a, b) => parseFloat(a.price) - parseFloat(b.price)
            );
            const formattedVariants = sortedVariants.map((v) => ({
              ...v,
              quantity:
                v.quantity_unit?.toLowerCase() === "size" || v.quantity_unit?.toLowerCase() === "color"
                  ? v.quantity
                  : String(parseFloat(v.quantity)),
              quantity_unit:
                v.quantity_unit?.toLowerCase() === "piece" ? "PC" : v.quantity_unit,
            }));
            setVariants(formattedVariants);
          } catch (err) { console.error("Variant error", err); }
        }

        if (user?.id && data?.id) {
          try {
            const favRes = await api.get(`/favorites/ids/?auth_id=${user.id}&product_id=${data.id}`);
            setIsFavorite(favRes.data?.is_favorite === true);
          } catch { }
        }

        if (data?.category) {
          try {
            const resSimilar = await api.get(`/productfilter/${data.category}/?page=1`);
            const list = resSimilar.data?.data || [];
            setSimilarProducts(list.filter((item) => item.id !== data.id));
          } catch (err) {
            console.error("Similar products error", err);
            setSimilarProducts([]);
          }
        } else {
          setSimilarProducts([]);
        }
      } catch (error) {
        console.error("Error fetching product:", error);
      } finally {
        setLoading(false);
        setLoadingSimilar(false);
      }
    };
    if (id) fetchProduct();
  }, [id, user?.id]);

  const toggleFavorite = async () => {
    if (!user) { toast.error("Please login first!"); navigate("/login"); return; }
    try {
      const res = await api.post(`/favorites/toggle/`, { product_id: product.id, auth_id: user.id });
      setIsFavorite(res.data?.is_favorite);
      toast.success(res.data?.is_favorite ? "Added to favorites ❤️" : "Removed from favorites");
    } catch { toast.error("Failed, please try again"); }
  };

  useEffect(() => {
    const getStock = async () => {
      try {
        const stockData = {};
        if (product?.id) {
          const resMain = await api.get(`/stock/?product_id=${product.id}`);
          stockData[product.id] = Number(resMain.data?.stock ?? product.stock_quantity ?? 0);
        }
        const similarIds = similarProducts.map((p) => p.id);
        if (similarIds.length > 0) {
          const results = await Promise.all(
            similarIds.map((pid) =>
              api.get(`/stock/?product_id=${pid}`)
                .then((r) => ({ id: pid, stock: Number(r.data?.stock ?? 0) }))
                .catch(() => ({ id: pid, stock: Number(similarProducts.find((sp) => sp.id === pid)?.stock_quantity ?? 0) }))
            )
          );
          results.forEach((r) => { stockData[r.id] = r.stock; });
        }
        setStocks((prev) => ({ ...prev, ...stockData }));
      } catch (error) {
        console.error("Stock fetch error:", error);
      }
    };
    if (product || similarProducts?.length > 0) getStock();
  }, [product, similarProducts]);

  // ── Sub-components ───────────────────────────────────────────

  const LoadingProduct = () => (
    <div className="container my-5 py-2">
      <div className="row">
        <div className="col-md-6 py-3"><Skeleton height={350} width={350} /></div>
        <div className="col-md-6 py-5">
          <Skeleton height={25} width={200} />
          <Skeleton height={80} />
          <Skeleton height={35} width={70} />
          <Skeleton height={40} width={110} />
          <Skeleton height={100} />
          <Skeleton height={35} width={110} inline={true} />
          <Skeleton className="mx-3" height={35} width={110} />
        </div>
      </div>
    </div>
  );

  const ShowProduct = () => {
    if (!product) return null;

    const hasOffer =
      product.offer_price !== null &&
      product.offer_price > 0 &&
      product.offer_price !== product.price;

    const available = Number(stocks[product.id] ?? product.stock_quantity ?? 0);
    const outOfStock = !product.is_available || available === 0;
    const stockLow = available <= 2 && available > 0;

    const variantLabel = (v) => {
      const unit = v.quantity_unit?.toLowerCase();
      return unit === "size" || unit === "color"
        ? v.quantity
        : `${v.quantity} ${v.quantity_unit}`;
    };

    return (
      <div className="container mt-3 mb-2 py-2">
        <div className="row align-items-center">

          {/* ── Image column ── */}
          <div className="col-md-6 col-sm-12 py-3 text-center">
            <div className="product-image-wrapper" style={{ position: "relative" }}>
              {user && (
                <motion.div
                  onClick={(e) => { e.stopPropagation(); toggleFavorite(); }}
                  initial={{ scale: 1 }}
                  animate={{ scale: isFavorite ? 1.15 : 1 }}
                  transition={{ duration: 0.25 }}
                  whileTap={{ scale: 0.9 }}
                  style={{ position: "absolute", top: "12px", right: "12px", cursor: "pointer", zIndex: 50 }}
                >
                  <motion.div
                    style={{
                      width: "30px", height: "30px", borderRadius: "50%",
                      backgroundColor: "#E9DCC3", boxShadow: "0 2px 6px #4A1F1A",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      backdropFilter: "blur(2px)", transition: "all 0.25s ease",
                    }}
                    whileHover={{ scale: 1.12, boxShadow: "0 2px 8px #4A1F1A" }}
                  >
                    {isFavorite ? (
                      <motion.span animate={{ scale: [1, 1.3, 1] }} transition={{ duration: 0.3 }} style={{ color: "#e63946", fontSize: "15px" }}>❤️</motion.span>
                    ) : (
                      <span style={{ color: "#6c757d", fontSize: "15px" }}>🤍</span>
                    )}
                  </motion.div>
                </motion.div>
              )}

              {stockError[product.id] && (
                <motion.div
                  initial={{ x: 80, opacity: 0 }}
                  animate={{ x: [80, 0, -6, 6, -6, 6, 0], opacity: 1 }}
                  transition={{ duration: 0.6, ease: "easeOut" }}
                  style={badgeStyleCurrent}
                >
                  Stock limit reached!
                </motion.div>
              )}

              <img
                className="img-fluid"
                src={product.product_image}
                alt={product.product_name}
                style={{ objectFit: "contain", maxHeight: "350px" }}
              />
              {outOfStock && <span className="stock-badge out-stock">Out of Stock</span>}
              {!outOfStock && stockLow && (
                <span className="stock-badge low-stock">Hurry! Only {available} left</span>
              )}
              {hasOffer && (
                <span className="offer-badge">{Math.round(product.offer_percentage)}% OFF</span>
              )}
            </div>
          </div>

          {/* ── Info column ── */}
          <div className="col-md-6 py-3">
            <h6 className="text-uppercase" style={{ color: "#8A4B27" }}>{product.category_name}</h6>
            <h3 className="fw-bold" style={{ color: "#4A1F1A" }}>{product.product_name}</h3>

            <p className="mb-2 d-flex align-items-center gap-2" style={{ color: "#000" }}>
              Rating: {product.average_rating}{" "}
              <i className="fa fa-star text-warning"></i>
              <button className="btn btn-link p-0 fw-semibold" onClick={goToReviews} style={{ color: "#4A1F1A" }}>
                View Reviews
              </button>
            </p>

            <h4 className="my-2" style={{ color: "#4A1F1A" }}>
              {hasOffer ? (
                <>
                  ₹{parseFloat(product.offer_price).toLocaleString()}{" "}
                  <small className="text-danger text-decoration-line-through">
                    ₹{parseFloat(product.price).toLocaleString()}
                  </small>
                </>
              ) : (
                `₹${parseFloat(product.price).toLocaleString()}`
              )}
            </h4>

            {/* ── Variant selector ── */}
            {variants.length > 0 && (
              <div className="variant-section mt-4">
                <h6 className="fw-bold mb-3">Available :</h6>
                <div className="d-flex flex-wrap gap-2">
                  {variants.map((v) => {
                    const vOos = isVariantOutOfStock(v);
                    const isActive = Number(id) === Number(v.id);

                    return (
                      <div key={v.id} style={{ position: "relative" }}>
                        <button
                          onClick={() => {
                            if (vOos) {
                              // Show notify banner for this OOS variant
                              setOosVariant(v);
                            } else {
                              navigate(`/product/${v.id}`);
                            }
                          }}
                          className={`variant-btn ${isActive ? "active" : ""} ${vOos ? "variant-btn-oos" : ""}`}
                          title={vOos ? "Out of stock — click to get notified" : variantLabel(v)}
                          aria-disabled={vOos}
                        >
                          <span className={vOos ? "variant-label-strike" : ""}>
                            {variantLabel(v)}
                          </span>
                          {vOos && <span className="variant-oos-tag">Out of stock</span>}
                        </button>
                      </div>
                    );
                  })}
                </div>

                {/* ── OOS Notify banner ── */}
                {oosVariant && (
                  <motion.div
                    key={oosVariant.id}
                    className="oos-notify-banner"
                    initial={{ opacity: 0, y: -8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.25 }}
                  >
                    <div className="oos-notify-icon">🔔</div>
                    <div className="oos-notify-text">
                      <strong>{variantLabel(oosVariant)}</strong> is currently out of stock
                      <span>Notify me when it's back in stock</span>
                    </div>
                    <button
                      className="oos-notify-cta"
                      onClick={() => openNotifyForVariant(oosVariant)}
                    >
                      Notify me
                    </button>
                    <button
                      className="oos-notify-close"
                      onClick={() => setOosVariant(null)}
                      aria-label="Dismiss"
                    >
                      ×
                    </button>
                  </motion.div>
                )}
              </div>
            )}

            {/* ── Action buttons ── */}
            <div className="d-flex flex-wrap mt-3">
              <div className="product-btns" onClick={(e) => e.stopPropagation()}>
                {outOfStock ? (
                  <div className="d-flex flex-column gap-2">
                    <span className="out-stock-label">Out of Stock</span>
                  </div>
                ) : getCartQty(product.id) === 0 ? (
                  <button className="btn-green" onClick={() => addItem(product)}>
                    Add to Cart
                  </button>
                ) : (
                  <div className="qty-box">
                    <button className="qty-btn" onClick={() => removeItem(product)}>
                      <i className="fas fa-minus"></i>
                    </button>
                    <span className="qty-value">{getCartQty(product.id)}</span>
                    <button className="qty-btn" onClick={() => addItem(product)} disabled={disabledQty[product.id]}>
                      <i className="fas fa-plus"></i>
                    </button>
                  </div>
                )}

                {!outOfStock && (
                  <button className="btn-checkout mb-2" onClick={initiateCheckout} disabled={getCartQty(product.id) === 0}>
                    Go to checkout
                  </button>
                )}

                <button onClick={handleShare} className="btn-checkout mb-2" title="Share product">
                  <i className="fas fa-paper-plane"></i>
                </button>
              </div>
            </div>

            {/* Description */}
            <div className="mt-3">
              <p className="small product-desc" style={{ color: "#000", whiteSpace: "pre-line", lineHeight: "1.5" }}>
                {product.product_description?.length > 250
                  ? product.product_description.slice(0, 250) + "..."
                  : product.product_description}
                {product.product_description?.length > 250 && (
                  <span onClick={() => setShowDescModal(true)} style={{ color: "#8A4B27", fontWeight: "600", cursor: "pointer", marginLeft: "6px" }}>
                    Read more
                  </span>
                )}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const LoadingSimilar = () => (
    <div className="scroll-container my-4 py-3">
      {[...Array(5)].map((_, idx) => (
        <div key={idx} className="similar-card">
          <Skeleton height={130} style={{ borderRadius: "12px" }} />
          <Skeleton height={16} width={100} style={{ margin: "8px auto 0" }} />
          <Skeleton height={14} width={70} style={{ margin: "4px auto" }} />
          <Skeleton height={32} width={100} style={{ margin: "6px auto" }} />
        </div>
      ))}
    </div>
  );

  const ShowSimilarProducts = () => (
    <div className="my-5 container">
      <h4 className="mb-3 fw-bold" style={{ color: "#4A1F1A" }}>You may also like</h4>
      <div className="scroll-container">
        {similarProducts.map((item) => {
          const hasOffer = item.offer_price !== null && Number(item.offer_price) > 0 && item.offer_price !== item.price;
          const available = Number(stocks[item.id] ?? item.stock_quantity ?? 0);
          const outOfStock = !item.is_available || available === 0;
          const stockLow = available <= 10 && available > 0;

          return (
            <div
              key={item.id}
              className="similar-card"
              style={outOfStock ? { opacity: 0.5, pointerEvents: "none" } : { cursor: "pointer" }}
              onClick={() => { if (!outOfStock) navigate(`/product/${item.id}`); }}
            >
              {hasOffer && <span className="sim-offer-badge">{Math.round(item.offer_percentage)}% OFF</span>}
              {outOfStock && <span className="sim-stock-badge out-stock">Out of Stock</span>}
              {!outOfStock && stockLow && <span className="sim-stock-badge low-stock">Only {available} left</span>}
              {stockError[item.id] && (
                <motion.div
                  initial={{ x: 80, opacity: 0 }}
                  animate={{ x: [80, 0, -6, 6, -6, 6, 0], opacity: 1 }}
                  transition={{ duration: 0.6, ease: "easeOut" }}
                  style={{ position: "absolute", top: "8px", left: "30%", transform: "translateX(-50%)", padding: "6px 14px", background: "#dc3545", color: "white", borderRadius: "20px", fontSize: "0.8rem", fontWeight: 600, zIndex: 99, whiteSpace: "nowrap", boxShadow: "0 3px 10px rgba(0,0,0,0.2)" }}
                >
                  Stock limit reached!
                </motion.div>
              )}

              <div className="sim-img-wrapper">
                <img src={item.product_image} alt={item.product_name} />
              </div>
              <div className="sim-content">
                <h6 className="sim-title">
                  {item.product_name.length > 15 ? item.product_name.substring(0, 15) + "..." : item.product_name}
                </h6>
                <div className="sim-price-wrap">
                  {hasOffer ? (
                    <>
                      <span className="sim-price-offer">₹{parseFloat(item.offer_price).toLocaleString()}</span>
                      <span className="sim-price-original"><s>₹{parseFloat(item.price).toLocaleString()}</s></span>
                    </>
                  ) : (
                    <span className="sim-price">₹{parseFloat(item.price).toLocaleString()}</span>
                  )}
                </div>
              </div>
              <div className="sim-btns" onClick={(e) => e.stopPropagation()}>
                {item.stock_quantity === 0 ? (
                  <span className="out-stock-label">Out of Stock</span>
                ) : getCartQty(item.id) === 0 ? (
                  <button className="btn-buy" onClick={() => addItem(item)}>Add to Cart</button>
                ) : (
                  <div className="qty-box">
                    <button className="qty-btn" onClick={() => removeItem(item)}><i className="fas fa-minus"></i></button>
                    <span className="qty-value">{getCartQty(item.id)}</span>
                    <button className="qty-btn" onClick={() => addItem(item)} disabled={disabledQty[item.id]}><i className="fas fa-plus"></i></button>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  return (
    <>
      <Navbar />
      <div style={{ backgroundColor: "#E9DCC3" }}>
        {loading || !product ? <LoadingProduct /> : <ShowProduct />}
        <div className="container pt-3 pb-5" ref={reviewRef}>
          {product?.id && <ReviewBox productId={product.id} />}
        </div>
        {loadingSimilar ? <LoadingSimilar /> : <ShowSimilarProducts />}
      </div>
      <Footer />

      {/* Stock Notify Modal */}
      <StockNotifyModal
        open={notifyOpen}
        onClose={() => setNotifyOpen(false)}
        product={notifyProduct}
        notifyForm={notifyForm}
        setNotifyForm={setNotifyForm}
        navigate={navigate}
      />

      <style>{`
        /* ===== VARIANT BUTTONS ===== */
        .variant-btn {
          border: 1.5px solid #4A1F1A;
          background-color: #fff;
          color: #4A1F1A;
          font-weight: 600;
          font-size: 0.85rem;
          padding: 6px 14px;
          border-radius: 8px;
          margin-bottom: 6px;
          transition: all 0.3s ease;
          box-shadow: 0 1px 2px rgba(0,0,0,0.05);
          cursor: pointer;
          display: flex;
          flex-direction: column;
          align-items: center;
          line-height: 1.2;
        }
        .variant-btn:hover:not(.variant-btn-oos) {
          background: linear-gradient(135deg, #CCA16A, #8A4B27);
          color: #fff;
          transform: translateY(-1px);
        }
        .variant-btn.active {
          background-color: #4A1F1A;
          border-color: #E9DCC3;
          color: #fff;
        }

        /* Out-of-stock variant button */
        .variant-btn-oos {
          background: #f5f0ea !important;
          border: 1.5px dashed #b0a898 !important;
          color: #999 !important;
          cursor: pointer !important;
        }
        .variant-btn-oos:hover {
          border-color: #dc3545 !important;
          background: #fff5f5 !important;
        }
        .variant-label-strike {
          text-decoration: line-through;
          opacity: 0.65;
        }
        .variant-oos-tag {
          display: block;
          font-size: 9px;
          font-weight: 700;
          color: #dc3545;
          letter-spacing: 0.2px;
          margin-top: 2px;
          text-decoration: none;
        }

        /* ===== OOS NOTIFY BANNER ===== */
        .oos-notify-banner {
          display: flex;
          align-items: center;
          gap: 10px;
          margin-top: 14px;
          background: #fff8f5;
          border: 1.5px solid #f0c4b0;
          border-radius: 12px;
          padding: 11px 14px;
        }
        .oos-notify-icon {
          font-size: 18px;
          flex-shrink: 0;
        }
        .oos-notify-text {
          flex: 1;
          font-size: 13px;
          color: #8A4B27;
          font-weight: 600;
          display: flex;
          flex-direction: column;
          gap: 2px;
        }
        .oos-notify-text span {
          font-size: 11px;
          font-weight: 400;
          color: #b06040;
        }
        .oos-notify-cta {
          background: #4A1F1A;
          color: #FAECE7;
          border: none;
          border-radius: 8px;
          padding: 7px 14px;
          font-size: 12px;
          font-weight: 700;
          cursor: pointer;
          white-space: nowrap;
          transition: background 0.15s;
          flex-shrink: 0;
        }
        .oos-notify-cta:hover { background: #712B13; }
        .oos-notify-close {
          background: none;
          border: none;
          font-size: 1.2rem;
          color: #aaa;
          cursor: pointer;
          line-height: 1;
          padding: 0 2px;
          flex-shrink: 0;
        }
        .oos-notify-close:hover { color: #4A1F1A; }

        /* ===== NOTIFY ME MAIN BUTTON (product itself OOS) ===== */
        .btn-notify-main {
          background: transparent;
          border: 2px solid #4A1F1A;
          color: #4A1F1A;
          border-radius: 8px;
          padding: 7px 18px;
          font-weight: 700;
          font-size: 0.88rem;
          cursor: pointer;
          transition: all 0.2s ease;
        }
        .btn-notify-main:hover {
          background: #4A1F1A;
          color: #FAECE7;
        }

        /* ===== MAIN PRODUCT IMAGE WRAPPER ===== */
        .product-image-wrapper {
          background: linear-gradient(145deg, #fff8f0, #E9DCC3);
          border: 2px solid #4A1F1A;
          border-radius: 16px;
          padding: 16px;
          position: relative;
          height: 420px;
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: hidden;
          box-shadow: 0 6px 24px rgba(74,31,26,0.15), inset 0 1px 0 rgba(255,255,255,0.6);
        }
        .product-image-wrapper img {
          max-height: 100%; max-width: 100%; width: 100%; height: 100%;
          object-fit: contain; object-position: center;
          display: block; border-radius: 10px; transition: transform 0.4s ease;
        }
        .product-image-wrapper:hover img { transform: scale(1.04); }

        .stock-badge {
          position: absolute; top: 14px; left: 0;
          padding: 5px 14px 5px 10px; font-size: 0.8rem; font-weight: 700;
          border-radius: 0 20px 20px 0; box-shadow: 0 2px 6px rgba(0,0,0,0.15);
          z-index: 9; letter-spacing: 0.2px;
        }
        .stock-badge.low-stock { background: #ffc107; color: #000; }
        .stock-badge.out-stock { background: #dc3545; color: #fff; }
        .offer-badge {
          position: absolute; top: 50px; right: 0;
          background: linear-gradient(135deg, #567a0f, #2E7D32); color: #fff;
          padding: 5px 10px 5px 14px; font-size: 0.85rem; font-weight: 700;
          border-radius: 20px 0 0 20px; box-shadow: 0 2px 6px rgba(0,0,0,0.15); z-index: 5;
        }

        /* ===== VARIANT SECTION ===== */
        .variant-section {
          border: 1px solid #4A1F1A; border-radius: 12px;
          background: #E9DCC3; padding: 12px 15px; margin-right: 20px;
          display: inline-block; box-shadow: 0 2px 6px rgba(0,0,0,0.05); margin-bottom: 18px;
        }
        .variant-section h6 { font-size: 0.95rem; font-weight: 700; color: #4A1F1A; letter-spacing: 0.3px; }
        @media (max-width: 768px) {
          .variant-section { text-align: center; width: 100%; margin-top: 15px; }
          .variant-btn { padding: 6px 12px; font-size: 0.8rem; }
          .oos-notify-banner { flex-wrap: wrap; }
        }

        /* ===== PRODUCT BUTTONS ===== */
        .product-btns { display: flex; flex-wrap: wrap; gap: 10px; padding-bottom: 10px; }
        .btn-green {
          background: linear-gradient(135deg, #8A4B27, #4A1F1A); color: #fff !important;
          border: none; border-radius: 8px; padding: 6px 18px; font-weight: 700; height: 40px;
          cursor: pointer; box-shadow: 0 4px 10px #4A1F1A; transition: all 0.25s ease;
        }
        .btn-green:hover { background: linear-gradient(135deg, #CCA16A, #8A4B27); transform: translateY(-2px); }
        .btn-green:active { transform: scale(0.97); }
        .btn-green:disabled { background: #d6d6d6 !important; color: #888 !important; cursor: not-allowed !important; box-shadow: none !important; transform: none !important; }
        .btn-checkout {
          background: linear-gradient(135deg, #8A4B27, #4A1F1A); color: #fff !important;
          border: none; border-radius: 8px; padding: 6px 20px; font-weight: 700; height: 40px;
          cursor: pointer; transition: all 0.3s ease;
        }
        .btn-checkout:hover { transform: translateY(-2px); background: linear-gradient(135deg, #CCA16A, #8A4B27); }
        .btn-checkout:disabled { background: #d6d6d6; color: #888 !important; cursor: not-allowed; transform: none; }

        .qty-box {
          display: flex; align-items: center; justify-content: center;
          gap: 12px; height: 40px; padding: 0 14px;
          background: #fff; border: 1.5px solid #4A1F1A; border-radius: 6px;
        }
        .qty-btn {
          width: 28px; height: 28px; background: #8A4B27; color: #fff !important;
          border: none; border-radius: 50%; font-size: 1rem; font-weight: 700;
          display: flex; align-items: center; justify-content: center;
          cursor: pointer; transition: all 0.2s ease;
        }
        .qty-btn:hover { transform: scale(1.1); }
        .qty-value { font-weight: 700; font-size: 1rem; min-width: 22px; text-align: center; color: #4A1F1A; }
        .out-stock-label { background: #dc3545; color: #fff; padding: 6px 16px; border-radius: 25px; font-weight: 600; font-size: 0.9rem; }

        /* ===== SIMILAR PRODUCTS ===== */
        .scroll-container {
          display: flex; overflow-x: auto; gap: 15px; padding-bottom: 12px;
          scrollbar-width: thin; scrollbar-color: #4A1F1A #E9DCC3;
        }
        .scroll-container::-webkit-scrollbar { height: 8px; }
        .scroll-container::-webkit-scrollbar-track { background: #E9DCC3; border-radius: 10px; }
        .scroll-container::-webkit-scrollbar-thumb { background: #2E7D32; border-radius: 10px; }
        .similar-card {
          flex: 0 0 160px; background: #E9DCC3; border-radius: 18px;
          border: 2px solid #6d4c41; padding: 10px; display: flex;
          flex-direction: column; justify-content: space-between;
          height: 280px; transition: all 0.3s ease; position: relative;
        }
        .similar-card:hover { transform: translateY(-5px); box-shadow: 0 8px 20px rgba(0,0,0,0.12); }
        .sim-img-wrapper { width: 100%; height: 180px; display: flex; align-items: center; justify-content: center; overflow: hidden; }
        .sim-img-wrapper img { width: 100%; height: 100%; object-fit: contain; transition: transform 0.3s ease; }
        .sim-content { text-align: center; padding: 4px 6px; }
        .sim-title { font-weight: 600; font-size: 0.92rem; color: #000; margin-bottom: 3px; }
        .sim-price-wrap { display: flex; align-items: baseline; justify-content: center; gap: 6px; margin-top: 4px; white-space: nowrap; }
        .sim-price { color: #000; font-weight: 700; font-size: 0.9rem; }
        .sim-price-offer { color: #4A1F1A; font-size: 1rem; font-weight: 700; }
        .sim-price-original { color: #dc3545; font-size: 0.78rem; }
        .sim-btns { display: flex; justify-content: center; padding-bottom: 4px; }
        .btn-buy { border: none; border-radius: 8px; padding: 5px 12px; font-size: 0.8rem; font-weight: 500; background: #4A1F1A; color: #fff; cursor: pointer; transition: background 0.2s; }
        .btn-buy:hover { background: #8A4B27; }
        .btn-buy:disabled { background: #ddd !important; color: #888 !important; cursor: not-allowed !important; }
        .sim-offer-badge { position: absolute; top: 10px; right: 0; background: linear-gradient(135deg, #567a0f, #2E7D32); color: #fff; padding: 2px 5px; font-size: 0.72rem; font-weight: 600; border-radius: 6px 0 0 6px; z-index: 5; }
        .sim-stock-badge { position: absolute; top: 10px; left: 0; padding: 3px 8px; font-size: 0.7rem; font-weight: 600; border-radius: 0 6px 6px 0; z-index: 6; }
        .sim-stock-badge.low-stock { background: #ffc107; color: #000; }
        .sim-stock-badge.out-stock { background: #dc3545; color: #fff; }

        /* ===== DESC MODAL ===== */
        .desc-modal { position: fixed; top: 0; left: 0; width: 100%; height: 100%; background: rgba(0,0,0,0.6); display: flex; justify-content: center; align-items: center; z-index: 99999; }
        .desc-modal-content { background: #E9DCC3; width: 90%; max-width: 600px; max-height: 80vh; overflow-y: auto; border-radius: 12px; border: 1.5px solid #4A1F1A; box-shadow: 0 4px 12px rgba(0,0,0,0.25); position: relative; animation: slideUpFade 0.3s ease; }
        @keyframes slideUpFade { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
        .desc-text { white-space: pre-line; font-size: 0.95rem; line-height: 1.6; color: #000; margin-top: 10px; }
        .modal-close-btn { position: absolute; top: 10px; right: 12px; background: none; border: none; font-size: 1.5rem; font-weight: 700; color: #4A1F1A; cursor: pointer; transition: transform 0.2s ease; }
        .modal-close-btn:hover { transform: scale(1.1); }
        .review-section { background: #fff; padding: 30px; border-radius: 12px; box-shadow: 0 2px 8px rgba(0,0,0,0.05); }
      `}</style>

      {showDescModal && (
        <div className="desc-modal">
          <div className="desc-modal-content p-4 rounded shadow">
            <button className="modal-close-btn" onClick={() => setShowDescModal(false)}>×</button>
            <h5 className="fw-bold mb-3" style={{ color: "#4A1F1A" }}>Product Description</h5>
            <div className="desc-text" style={{ whiteSpace: "pre-line", color: "#000", fontSize: "0.95rem", lineHeight: "1.5rem" }}>
              {product.product_description}
            </div>
            <div className="modal-footer justify-content-center">
              <button
                style={{ background: "linear-gradient(135deg, #8A4B27, #4A1F1A)", color: "#fff", border: "none", borderRadius: "8px", padding: "6px 20px", fontWeight: 700, cursor: "pointer" }}
                onClick={() => setShowDescModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Product;